import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  Paper,
  Container,
  CircularProgress,
  InputAdornment,
  IconButton,
} from "@mui/material";
import { FiLock, FiArrowLeft, FiEye, FiEyeOff } from "react-icons/fi";
import api from "../api/axiosConfig";
import logo1 from "../images/logo.jpg";

const ChangePassword = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [message, setMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [userInfo, setUserInfo] = useState(null);
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Determine if this is first login by checking for a token.
  // If a user is on this page and has a token, it's a regular change.
  // If they are here from a redirect with a 'first login' flag, we trust that.
  const isFirstLogin =
    location.state?.isFirstLogin ||
    localStorage.getItem("isFirstLogin") === "true";

  // Check if the user is genuinely logged in (for the 'Go Back' button logic)
  // A logged-in user will have a token and the first login flag will be false.
  const isLoggedInUser = !!localStorage.getItem("userToken") && !isFirstLogin;

  useEffect(() => {
    // Get user info for display
    const userName = localStorage.getItem("name");
    const userRole = localStorage.getItem("userRole");

    if (userName && userRole) {
      setUserInfo({ name: userName, role: userRole });
    }

    if (location.state?.isFirstLogin) {
      localStorage.setItem("isFirstLogin", "true");
    }
  }, [location]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isFirstLogin) {
      if (!newPassword || !confirmPassword) {
        setMessage("Please fill in all fields.");
        return;
      }
    } else {
      if (!oldPassword || !newPassword || !confirmPassword) {
        setMessage("Please fill in all fields.");
        return;
      }
    }

    if (newPassword.length < 6) {
      setMessage("Password must be at least 6 characters long.");
      return;
    }

    if (newPassword !== confirmPassword) {
      setMessage("New password and confirmation do not match.");
      return;
    }

    if (!isFirstLogin && oldPassword === newPassword) {
      setMessage("New password cannot be the same as current password.");
      return;
    }

    setIsLoading(true);
    setMessage("");

    try {
      const token = localStorage.getItem("userToken");
      if (!token) {
        setMessage("Session expired. Please login again.");
        setTimeout(() => navigate("/user-login"), 2000);
        return;
      }

      let response;

      if (isFirstLogin) {
        response = await api.post(
          "/api/auth/change-first-password",
          { newPassword },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else {
        response = await api.put(
          "/api/auth/me/change-password",
          { oldPassword, newPassword },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }

      setMessage(response.data.message || "Password changed successfully!");

      if (isFirstLogin) {
        // First time success: Clear session and force re-login
        localStorage.removeItem("isFirstLogin");
        localStorage.removeItem("userToken");
        localStorage.removeItem("userRole");
        localStorage.removeItem("name");

        setTimeout(() => {
          navigate("/user-login", {
            state: {
              message:
                "Password set successfully! Please login with your new password.",
            },
          });
        }, 2000);
      } else {
        // Regular user success: Just go back to the previous page (e.g., Settings)
        setTimeout(() => {
          navigate(-1); // Go back one page in history
        }, 1500);
      }
    } catch (error) {
      console.error("Password change error:", error);
      const errorMessage =
        error.response?.data?.message || "An error occurred. Please try again.";
      setMessage(errorMessage);

      if (error.response?.status === 401) {
        localStorage.clear();
        setTimeout(() => navigate("/user-login"), 2000);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // This handler is now ONLY for the first-login scenario
  const handleBackToLogin = () => {
    localStorage.clear(); // Clear everything to be safe
    navigate("/user-login");
  };

  return (
    <Container component="main" maxWidth="sm">
      <Box
        sx={{
          marginTop: 8,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <Paper
          elevation={3}
          sx={{
            padding: 4,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            width: "100%",
          }}
        >
          <img
            src={logo1}
            alt="Logo"
            style={{ width: 80, height: 80, marginBottom: 16 }}
          />

          {userInfo && (
            <Box sx={{ textAlign: "center", mb: 2 }}>
              <Typography variant="h6" gutterBottom>
                Welcome, {userInfo.name}!
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {userInfo.role.replace(/_/g, " ").toUpperCase()}
              </Typography>
            </Box>
          )}

          <Typography component="h1" variant="h4" gutterBottom>
            {isFirstLogin ? "Set Your Password" : "Change Password"}
          </Typography>

          <Typography
            variant="body2"
            color="text.secondary"
            align="center"
            sx={{ mb: 3 }}
          >
            {isFirstLogin
              ? "Welcome! For security reasons, please set a new password for your account."
              : "Enter your current password and a new one to update your account."}
          </Typography>

          {message && (
            <Alert
              severity={
                message.toLowerCase().includes("successfully") ||
                message.toLowerCase().includes("please login")
                  ? "success"
                  : "error"
              }
              sx={{ width: "100%", mb: 2 }}
            >
              {message}
            </Alert>
          )}

          <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{ mt: 1, width: "100%" }}
          >
            {!isFirstLogin && (
              <TextField
                margin="normal"
                required
                fullWidth
                name="oldPassword"
                label="Current Password"
                type={showOldPassword ? "text" : "password"}
                id="oldPassword"
                value={oldPassword}
                onChange={(e) => setOldPassword(e.target.value)}
                disabled={isLoading}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => setShowOldPassword(!showOldPassword)}
                        edge="end"
                      >
                        {showOldPassword ? <FiEyeOff /> : <FiEye />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            )}

            <TextField
              margin="normal"
              required
              fullWidth
              name="newPassword"
              label="New Password"
              type={showNewPassword ? "text" : "password"}
              id="newPassword"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              disabled={isLoading}
              helperText="Must be at least 6 characters long"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      edge="end"
                    >
                      {showNewPassword ? <FiEyeOff /> : <FiEye />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              margin="normal"
              required
              fullWidth
              name="confirmPassword"
              label="Confirm New Password"
              type={showConfirmPassword ? "text" : "password"}
              id="confirmPassword"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              disabled={isLoading}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      edge="end"
                    >
                      {showConfirmPassword ? <FiEyeOff /> : <FiEye />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2 }}
              disabled={isLoading}
              startIcon={
                isLoading ? <CircularProgress size={20} /> : <FiLock />
              }
            >
              {isLoading
                ? "Processing..."
                : isFirstLogin
                ? "Set New Password"
                : "Change Password"}
            </Button>

            {/* <<< START OF CHANGES >>> */}
            {/* Conditionally render the correct back button */}
            {isLoggedInUser ? (
              // This button shows for a logged-in user
              <Button
                fullWidth
                variant="outlined"
                onClick={() => navigate(-1)} // Just go back, no logout
                disabled={isLoading}
                startIcon={<FiArrowLeft />}
              >
                Go Back
              </Button>
            ) : (
              // This button shows for a first-time user who must log in again
              <Button
                fullWidth
                variant="outlined"
                onClick={handleBackToLogin} // This logs the user out
                disabled={isLoading}
                startIcon={<FiArrowLeft />}
              >
                Back to Login
              </Button>
            )}
            {/* <<< END OF CHANGES >>> */}
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default ChangePassword;
